import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { CheckCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form"
import { Checkbox } from "@/components/ui/checkbox"
import { contestRegistrationSchema, type ContestRegistrationFormValues } from "./contestSchema"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface ContestRegistrationFormProps {
  registrationId?: string
  onUpdate?: () => void
}

export const ContestRegistrationForm = ({ registrationId, onUpdate }: ContestRegistrationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  
  const form = useForm<ContestRegistrationFormValues>({
    resolver: zodResolver(contestRegistrationSchema),
    defaultValues: {
      schoolName: "",
      schoolAddress: "",
      schoolPostalCode: "",
      schoolCity: "",
      schoolPhone: "",
      schoolEmail: "",
      academy: "Aix-Marseille",
      principalName: "",
      teacherName: "",
      teacherFunction: "",
      teacherPhone: "",
      teacherEmail: "",
      classLevel: undefined,
      className: "",
      participantsCount: undefined,
      totalStudentsCount: undefined,
      category: undefined,
      principalApproval: false,
    },
  })

  // Charger les données existantes si un ID est fourni
  useEffect(() => {
    const loadRegistration = async () => {
      if (!registrationId) return

      try {
        const { data, error } = await supabase
          .from('contest_registrations')
          .select('*')
          .eq('id', registrationId)
          .maybeSingle()

        if (error) throw error

        if (data) {
          setIsEditing(true)
          form.reset({
            schoolName: data.school_name,
            schoolAddress: data.school_address,
            schoolPostalCode: data.school_postal_code,
            schoolCity: data.school_city,
            schoolPhone: data.school_phone,
            schoolEmail: data.school_email,
            academy: data.academy,
            principalName: data.principal_name,
            teacherName: data.teacher_name,
            teacherFunction: data.teacher_function,
            teacherPhone: data.teacher_phone,
            teacherEmail: data.teacher_email,
            classLevel: data.class_level as "4e" | "3e" | "2de",
            className: data.class_name || "",
            participantsCount: data.participants_count,
            totalStudentsCount: data.total_students_count,
            category: data.category as "mots" | "images" | "son",
            principalApproval: true, // Already approved if editing
          })
          toast.success("Inscription chargée avec succès !")
        }
      } catch (error) {
        console.error('Error loading registration:', error)
        toast.error("Erreur lors du chargement de l'inscription")
      }
    }

    loadRegistration()
  }, [registrationId, form])

  const onSubmit = async (data: ContestRegistrationFormValues) => {
    setIsSubmitting(true)
    
    try {
      const registrationData = {
        school_name: data.schoolName,
        school_address: data.schoolAddress,
        school_postal_code: data.schoolPostalCode,
        school_city: data.schoolCity,
        school_phone: data.schoolPhone,
        school_email: data.schoolEmail,
        academy: data.academy,
        principal_name: data.principalName,
        teacher_name: data.teacherName,
        teacher_function: data.teacherFunction,
        teacher_phone: data.teacherPhone,
        teacher_email: data.teacherEmail,
        class_level: data.classLevel,
        class_name: data.className,
        participants_count: data.participantsCount,
        total_students_count: data.totalStudentsCount,
        category: data.category,
      }

      if (isEditing && registrationId) {
        // Mise à jour
        const { error } = await supabase
          .from('contest_registrations')
          .update(registrationData)
          .eq('id', registrationId)

        if (error) throw error

        toast.success("Inscription modifiée avec succès !")
        setRegistrationSuccess(true)
        onUpdate?.()
      } else {
        // Création
        const { error } = await supabase
          .from('contest_registrations')
          .insert(registrationData)

        if (error) throw error

        toast.success("Inscription enregistrée avec succès !")
        setRegistrationSuccess(true)
        form.reset()
      }
    } catch (error) {
      console.error('Error submitting registration:', error)
      toast.error("Une erreur s'est produite lors de l'inscription")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (registrationSuccess) {
    return (
      <Card className="border-green-500 bg-green-50">
        <CardContent className="pt-8 pb-8">
          <div className="flex flex-col items-center text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-600" />
            <h2 className="text-2xl font-bold text-green-800">
              Inscription enregistrée avec succès !
            </h2>
            <p className="text-green-700 max-w-md">
              Votre inscription au concours "Exprime la Charte" a bien été enregistrée.
            </p>
            <Button 
              onClick={() => setRegistrationSuccess(false)}
              variant="outline"
              className="mt-4"
            >
              Faire une nouvelle inscription
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* School Information */}
        <Card>
          <CardHeader>
            <CardTitle>1. Informations sur l'établissement scolaire</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="schoolName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom de l'établissement *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="schoolAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Adresse *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="schoolPostalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code postal *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="schoolCity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ville *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="schoolPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone de l'établissement *</FormLabel>
                  <FormControl>
                    <Input {...field} type="tel" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="academy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Académie *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Sélectionnez une académie" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background max-h-60 z-50">
                      <SelectItem value="Aix-Marseille">Aix-Marseille</SelectItem>
                      <SelectItem value="Amiens">Amiens</SelectItem>
                      <SelectItem value="Besançon">Besançon</SelectItem>
                      <SelectItem value="Bordeaux">Bordeaux</SelectItem>
                      <SelectItem value="Clermont-Ferrand">Clermont-Ferrand</SelectItem>
                      <SelectItem value="Corse">Corse</SelectItem>
                      <SelectItem value="Créteil">Créteil</SelectItem>
                      <SelectItem value="Dijon">Dijon</SelectItem>
                      <SelectItem value="Grenoble">Grenoble</SelectItem>
                      <SelectItem value="Guadeloupe">Guadeloupe</SelectItem>
                      <SelectItem value="Guyane">Guyane</SelectItem>
                      <SelectItem value="La Réunion">La Réunion</SelectItem>
                      <SelectItem value="Lille">Lille</SelectItem>
                      <SelectItem value="Limoges">Limoges</SelectItem>
                      <SelectItem value="Lyon">Lyon</SelectItem>
                      <SelectItem value="Martinique">Martinique</SelectItem>
                      <SelectItem value="Mayotte">Mayotte</SelectItem>
                      <SelectItem value="Montpellier">Montpellier</SelectItem>
                      <SelectItem value="Nancy-Metz">Nancy-Metz</SelectItem>
                      <SelectItem value="Nantes">Nantes</SelectItem>
                      <SelectItem value="Nice">Nice</SelectItem>
                      <SelectItem value="Normandie">Normandie</SelectItem>
                      <SelectItem value="Nouvelle-Calédonie">Nouvelle-Calédonie</SelectItem>
                      <SelectItem value="Orléans-Tours">Orléans-Tours</SelectItem>
                      <SelectItem value="Paris">Paris</SelectItem>
                      <SelectItem value="Poitiers">Poitiers</SelectItem>
                      <SelectItem value="Polynésie Française">Polynésie Française</SelectItem>
                      <SelectItem value="Reims">Reims</SelectItem>
                      <SelectItem value="Rennes">Rennes</SelectItem>
                      <SelectItem value="Saint-Pierre et Miquelon (Services de l'EN)">Saint-Pierre et Miquelon (Services de l'EN)</SelectItem>
                      <SelectItem value="Strasbourg">Strasbourg</SelectItem>
                      <SelectItem value="Toulouse">Toulouse</SelectItem>
                      <SelectItem value="Versailles">Versailles</SelectItem>
                      <SelectItem value="Wallis et Futuna">Wallis et Futuna</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="principalName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chef d'établissement (nom, prénom) *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="schoolEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email établissement *</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Teacher Information */}
        <Card>
          <CardHeader>
            <CardTitle>2. Informations sur l'enseignant référent du projet</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="teacherName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom / Prénom *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="teacherFunction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fonction (discipline enseignée) *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="teacherPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone *</FormLabel>
                  <FormControl>
                    <Input {...field} type="tel" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="teacherEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Class Information */}
        <Card>
          <CardHeader>
            <CardTitle>3. Classe concernée</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="classLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Niveau de classe *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Sélectionnez un niveau" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background max-h-60 z-50">
                      <SelectItem value="4e">4e</SelectItem>
                      <SelectItem value="3e">3e</SelectItem>
                      <SelectItem value="2de">2de</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="className"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom de la classe *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ex: 4e A, 3e Sciences, 2de 7..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="participantsCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre d'élèves participants *</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="number" 
                        min="1"
                        onChange={(e) => field.onChange(parseInt(e.target.value) || undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="totalStudentsCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre total d'élèves dans la classe *</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="number" 
                        min="1"
                        onChange={(e) => field.onChange(parseInt(e.target.value) || undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Category */}
        <Card>
          <CardHeader>
            <CardTitle>4. Catégorie choisie</CardTitle>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Choisissez une catégorie *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Sélectionnez une catégorie" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background max-h-60 z-50">
                      <SelectItem value="mots">Mots</SelectItem>
                      <SelectItem value="images">Images (incluant vidéo)</SelectItem>
                      <SelectItem value="son">Son</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Principal Approval */}
        <Card>
          <CardContent className="pt-6">
            <FormField
              control={form.control}
              name="principalApproval"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Je certifie avoir obtenu l'accord du chef d'établissement pour la participation au concours "exprime ta charte" au cours de l'année 2026 *
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button 
            type="submit" 
            size="lg"
            disabled={isSubmitting}
            className="w-full md:w-auto bg-[#4AA9E6] hover:bg-[#3A99D6] text-white"
          >
            {isSubmitting 
              ? "Envoi en cours..." 
              : isEditing 
                ? "Mettre à jour l'inscription" 
                : "Soumettre l'inscription"
            }
          </Button>
        </div>
      </form>
    </Form>
  )
}
